import { useRef, forwardRef } from 'react';
import PropTypes from 'prop-types';
import useSelect from 'use-select';
import { matchSorter } from 'match-sorter';
import { FixedSizeList } from 'react-window';
import clsx from 'clsx';
import SelectControlOption from './SelectControlOption';

const SelectControlOptionForwardRef = forwardRef(({ index, style }, ref) => {
  const option = visibleOptions[index];

  if (!visibleOptions.length) {
    return (
      <div ref={ref} style={style}>
        No se encontraron opciones...
      </div>
    );
  }

  return (
    <SelectControlOption
      {...getOptionProps({
        index,
        option,
        ref,
        style,
        highlighted: option === highlightedOption,
        selected: option === selectedOption,
      })}
      className={optionClassName}
      highlightedClassName={optionHighlightedClassName}
      selectedClassName={optionSelectedClassName}
    >
      {option.label}
    </SelectControlOption>
  );
});

SelectControlOptionForwardRef.displayName = 'SelectControlOptionForwardRef';

const defaultProps = {
  value: null,
  options: [],
  pageSize: 10,
  itemHeight: 28,
  className: null,
  placeholder: 'Seleccionar',
  inputClassName: null,
  optionsClassName: null,
  optionClassName: null,
  optionHighlightedClassName: null,
  optionSelectedClassName: null,
  onChange: () => { },
};

const propTypes = {
  value: PropTypes.string,
  options: PropTypes.arrayOf(PropTypes.object),
  pageSize: PropTypes.number,
  itemHeight: PropTypes.number,
  className: PropTypes.string,
  placeholder: PropTypes.string,
  inputClassName: PropTypes.string,
  optionsClassName: PropTypes.string,
  optionClassName: PropTypes.string,
  optionHighlightedClassName: PropTypes.string,
  optionSelectedClassName: PropTypes.string,
  onChange: PropTypes.func,
};

export default function SelectControl({
  value,
  options,
  onChange,
  pageSize,
  className,
  placeholder,
  inputClassName,
  optionsClassName,
  optionClassName,
  optionHighlightedClassName,
  optionSelectedClassName,
  itemHeight,
}) {
  const reactWindowInstanceRef = useRef();
  const optionsRef = useRef();

  const scrollToIndex = (index) => {
    if (!reactWindowInstanceRef.current) {
      return;
    }
    reactWindowInstanceRef.current.scrollToItem(index);
  };

  const shiftAmount = pageSize;

  const {
    visibleOptions,
    selectedOption,
    highlightedOption,
    getInputProps,
    getOptionProps,
    isOpen
  } = useSelect({
    options,
    value,
    onChange,
    scrollToIndex,
    optionsRef,
    shiftAmount,
    filterFn: (options, value) => {
      return matchSorter(options, value, { keys: ['label'] });
    }
  });

  const height = Math.max(Math.min(pageSize, visibleOptions.length), 1) * itemHeight;

  return (
    <div className={clsx('relative max-w-full', className)}>
      <input placeholder={placeholder} className={clsx('max-w-full', inputClassName)} {...getInputProps()} />
      {isOpen && (
        <div className={clsx('absolute top-0 left-0 right-0 z-10', optionsClassName)} ref={optionsRef}>
          <FixedSizeList
            ref={reactWindowInstanceRef}
            height={height}
            itemCount={visibleOptions.length || 1}
            itemSize={itemHeight}
          >
            {SelectControlOptionForwardRef}
          </FixedSizeList>
        </div>
      )}
    </div>
  );
}

SelectControl.defaultProps = defaultProps;
SelectControl.propTypes = propTypes;
